import { supabase } from '../lib/supabase';

/**
 * Finds an existing chat between two users by their user IDs.
 * This ensures that chats are grouped by user identity, not by display name or vendor status.
 * 
 * @param userId1 - First user's ID
 * @param userId2 - Second user's ID
 * @returns The chat ID if found, null otherwise
 */
export async function findExistingChatBetweenUsers(
  userId1: string,
  userId2: string
): Promise<string | null> {
  try {
    console.log(`[CHAT-UTILS] Looking for existing chat between users: ${userId1} and ${userId2}`);

    // Get all chats where userId1 is a participant
    const { data: user1Chats, error: user1Error } = await supabase
      .from('chat_participants')
      .select('chat_id')
      .eq('user_id', userId1);

    if (user1Error) {
      console.error('[CHAT-UTILS] Error fetching chats for user1:', user1Error);
      throw user1Error;
    }

    if (!user1Chats || user1Chats.length === 0) {
      console.log('[CHAT-UTILS] No chats found for user1');
      return null;
    }

    const user1ChatIds = user1Chats.map(c => c.chat_id);
    console.log(`[CHAT-UTILS] User1 is in ${user1ChatIds.length} chats`);

    // Find chats where userId2 is also a participant (from the chats userId1 is in)
    const { data: sharedChats, error: sharedError } = await supabase
      .from('chat_participants')
      .select('chat_id')
      .eq('user_id', userId2)
      .in('chat_id', user1ChatIds);

    if (sharedError) {
      console.error('[CHAT-UTILS] Error finding shared chats:', sharedError);
      throw sharedError;
    }

    if (!sharedChats || sharedChats.length === 0) {
      console.log('[CHAT-UTILS] No shared chat found between the two users');
      return null;
    }

    // Return the first shared chat (there should typically only be one)
    const existingChatId = sharedChats[0].chat_id;
    console.log(`[CHAT-UTILS] Found existing chat: ${existingChatId}`);
    return existingChatId;
  } catch (error) {
    console.error('[CHAT-UTILS] Error in findExistingChatBetweenUsers:', error);
    throw error;
  }
}

/**
 * Creates a new chat between two users, optionally associating it with a vendor.
 * 
 * @param userId1 - First user's ID
 * @param userId2 - Second user's ID
 * @param vendorId - Optional vendor ID to associate with the chat
 * @returns The newly created chat ID
 */
export async function createChatBetweenUsers(
  userId1: string,
  userId2: string,
  vendorId?: string
): Promise<string> {
  try {
    console.log(`[CHAT-UTILS] Creating new chat between users: ${userId1} and ${userId2}${vendorId ? ` (vendor: ${vendorId})` : ''}`);

    // Create the chat
    const { data: newChat, error: chatError } = await supabase
      .from('chats')
      .insert({
        vendor_id: vendorId || null,
      })
      .select()
      .single();

    if (chatError) {
      console.error('[CHAT-UTILS] Error creating chat:', chatError);
      throw chatError;
    }

    console.log(`[CHAT-UTILS] Created chat: ${newChat.id}`);

    // Add both users as participants
    const { error: participantsError } = await supabase
      .from('chat_participants')
      .insert([
        { chat_id: newChat.id, user_id: userId1 },
        { chat_id: newChat.id, user_id: userId2 },
      ]);

    if (participantsError) {
      console.error('[CHAT-UTILS] Error adding participants:', participantsError);
      // Try to clean up the chat if participants couldn't be added
      await supabase.from('chats').delete().eq('id', newChat.id);
      throw participantsError;
    }

    console.log(`[CHAT-UTILS] Successfully created chat ${newChat.id} with both participants`);
    return newChat.id;
  } catch (error) {
    console.error('[CHAT-UTILS] Error in createChatBetweenUsers:', error);
    throw error;
  }
}

/**
 * Finds an existing chat or creates a new one between two users.
 * This is the main function to use when starting a chat - it ensures
 * that users always see the same chat thread regardless of profile changes.
 * 
 * @param currentUserId - The current user's ID
 * @param otherUserId - The other user's ID
 * @param vendorId - Optional vendor ID to associate with new chats
 * @returns The chat ID (existing or newly created)
 */
export async function findOrCreateChat(
  currentUserId: string,
  otherUserId: string,
  vendorId?: string
): Promise<string> {
  // Prevent self-chats - users shouldn't be able to chat with themselves
  if (currentUserId === otherUserId) {
    console.error('[CHAT-UTILS] Cannot create a chat with yourself');
    throw new Error('Cannot start a chat with yourself');
  }

  // First, try to find an existing chat
  const existingChatId = await findExistingChatBetweenUsers(currentUserId, otherUserId);
  
  if (existingChatId) {
    console.log(`[CHAT-UTILS] Using existing chat: ${existingChatId}`);
    return existingChatId;
  }

  // No existing chat found, create a new one
  console.log('[CHAT-UTILS] No existing chat found, creating new one');
  return createChatBetweenUsers(currentUserId, otherUserId, vendorId);
}

